/**
 * useCommodities — Zustand store for the Commodities Dashboard.
 * Fetches metals, energy, and agriculture prices.
 *
 * Debounce: won't re-fetch if data was fetched less than 30s ago.
 * Stale data: keeps last known data on error / during background refresh.
 */
import { create } from 'zustand'
import { getCommoditiesDashboard } from '@/lib/api'
import type { CommoditiesDashboard } from '@/lib/types'
import { useEffect } from 'react'

const DEBOUNCE_MS = 30_000

interface CommoditiesState {
  dashboard: CommoditiesDashboard | null
  loading: boolean
  refreshing: boolean
  error: string | null
  _lastFetchedAt: number

  fetch: (force?: boolean) => Promise<void>
}

export const useCommoditiesStore = create<CommoditiesState>()((set, get) => ({
  dashboard: null,
  loading: false,
  refreshing: false,
  error: null,
  _lastFetchedAt: 0,

  fetch: async (force = false) => {
    const now = Date.now()
    const state = get()

    if (!force && state._lastFetchedAt > 0 && now - state._lastFetchedAt < DEBOUNCE_MS) {
      return
    }

    const hasData = state.dashboard !== null
    if (hasData) {
      set({ refreshing: true, error: null })
    } else {
      set({ loading: true, error: null })
    }

    try {
      const dashboard = await getCommoditiesDashboard()
      set({
        dashboard,
        loading: false,
        refreshing: false,
        error: null,
        _lastFetchedAt: Date.now(),
      })
    } catch (e) {
      set({ error: String(e), loading: false, refreshing: false })
    }
  },
}))

/** Drop-in hook: fetches commodities on mount, auto-refreshes every 30s. */
export function useCommoditiesWithRefresh(intervalMs = 30_000) {
  const store = useCommoditiesStore()

  useEffect(() => {
    store.fetch()
    const id = setInterval(() => store.fetch(), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs]) // eslint-disable-line

  return store
}
